import React, { useState, useEffect, useCallback } from "react";
import Chart from "react-apexcharts";
import { getAllProgress } from "../../services/ProgressService";

const ProgressBar = () => {
  const [progressData, setProgressData] = useState({
    "Good progress": [],
    "Moderate progress": [],
    "Poor progress": [],
  });
  const [months, setMonths] = useState([]);

  const determineProgressCategory = useCallback((progress) => {
    const study = progress.studyId;

    const submitThesisDate = progress.submitThesis
      ? new Date(progress.submitThesis)
      : null;
    const studyFinishDate =
      study && study.finishDate ? new Date(study.finishDate) : null;
    const studyCompletionDate = progress.studyComplition
      ? new Date(progress.studyComplition)
      : null;

    const meetProgressPlanYes = progress.meetProgressPlan === "YES";
    const submitThesisBeforeFinishDate =
      submitThesisDate && studyFinishDate && submitThesisDate < studyFinishDate;
    const studyCompletionBeforeFinishDate =
      studyCompletionDate &&
      studyFinishDate &&
      studyCompletionDate < studyFinishDate;

    let criteriaMetCount = 0;
    if (meetProgressPlanYes) criteriaMetCount++;
    if (submitThesisBeforeFinishDate) criteriaMetCount++;
    if (studyCompletionBeforeFinishDate) criteriaMetCount++;

    if (criteriaMetCount === 3) {
      return "Good progress";
    } else if (criteriaMetCount === 2) {
      return "Moderate progress";
    } else {
      return "Poor progress";
    }
  }, []);

  const fetchProgressData = useCallback(async () => {
    try {
      const progressListResponse = await getAllProgress();
      const progressList = Array.isArray(progressListResponse.data)
        ? progressListResponse.data
        : [];

      if (progressList.length === 0) {
        console.warn("No progress data found.");
        setMonths([]);
        setProgressData({
          "Good progress": [],
          "Moderate progress": [],
          "Poor progress": [],
        });
        return;
      }

      const uniqueMonths = new Set();
      const progressByMonth = {
        "Good progress": {},
        "Moderate progress": {},
        "Poor progress": {},
      };

      progressList.forEach((progress) => {
        if (!progress.progressDate) return;

        const progressDate = new Date(progress.progressDate);
        const month = progressDate.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });

        uniqueMonths.add(month);

        const category = determineProgressCategory(progress);

        progressByMonth[category][month] =
          (progressByMonth[category][month] || 0) + 1;
      });

      const sortedMonths = Array.from(uniqueMonths).sort((a, b) => {
        const dateA = new Date(`01 ${a}`);
        const dateB = new Date(`01 ${b}`);
        return dateA.getTime() - dateB.getTime();
      });
      setMonths(sortedMonths);

      const goodData = sortedMonths.map(
        (month) => progressByMonth["Good progress"][month] || 0
      );
      const moderateData = sortedMonths.map(
        (month) => progressByMonth["Moderate progress"][month] || 0
      );
      const poorData = sortedMonths.map(
        (month) => progressByMonth["Poor progress"][month] || 0
      );

      setProgressData({
        "Good progress": goodData,
        "Moderate progress": moderateData,
        "Poor progress": poorData,
      });
    } catch (error) {
      console.error("Error fetching or processing data:", error);
      setMonths([]);
      setProgressData({
        "Good progress": [],
        "Moderate progress": [],
        "Poor progress": [],
      });
    }
  }, [determineProgressCategory]);

  useEffect(() => {
    fetchProgressData();
  }, [fetchProgressData]);

  const chartColors = ["#4CAF50", "#F3A23A", "#F94144"];

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        columnWidth: "55%",
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 0 ? val : "";
      },
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val, { seriesIndex, w }) {
          return `${w.globals.seriesNames[seriesIndex]}: ${val}`;
        },
      },
    },
    xaxis: {
      categories: months,
      title: {
        text: "Month",
      },
    },
    yaxis: {
      title: {
        text: "Progress Count",
      },
      min: 0,
    },
    fill: {
      opacity: 1,
    },
    colors: chartColors,
    legend: {
      position: "top",
      horizontalAlign: "left",
      labels: {
        colors: "#333",
      },
    },
  };

  const seriesData = [
    { name: "Good progress", data: progressData["Good progress"] },
    { name: "Moderate progress", data: progressData["Moderate progress"] },
    { name: "Poor progress", data: progressData["Poor progress"] },
  ];

  return (
    <div>
      {months.length > 0 ? (
        <Chart options={options} series={seriesData} type="bar" height={380} />
      ) : (
        <div style={{ textAlign: "center", padding: "50px" }}>
          Loading or No Data Available...
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
